import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '@common/routes/index';

const AUTH_REDIRECT_STORAGE_KEY = 'kemu.auth.redirectUrl';

type PreservedLocation = {
  pathname: string;
  search?: string;
};

/**
 * Builds an app-relative redirect URL from a location-like object.
 * @param preservedLocation - Location data to serialize.
 * @returns App-relative URL including the query string.
 */
const buildRedirectUrl = (preservedLocation: PreservedLocation): string => {
  return `${preservedLocation.pathname}${preservedLocation.search || ''}`;
};

/**
 * Checks whether a pathname belongs to the auth flow.
 * @param pathname - Pathname to validate.
 * @returns True when the pathname points to an auth route.
 */
const isAuthPath = (pathname: string): boolean => {
  const authBaseRoute = routes.auth.getAuthBaseRoute();
  return pathname === authBaseRoute || pathname.startsWith(`${authBaseRoute}/`);
};

/**
 * Validates and normalizes a redirect target.
 * @param redirectUrl - Redirect target to inspect.
 * @param fallbackUrl - Safe fallback URL to use when the target is invalid.
 * @returns A sanitized app-relative redirect target.
 */
const sanitizeRedirectUrl = (redirectUrl: string | null | undefined, fallbackUrl: string): string => {
  if (!redirectUrl || !redirectUrl.startsWith('/')) {
    return fallbackUrl;
  }

  try {
    const url = new URL(redirectUrl, window.location.origin);

    if (url.origin !== window.location.origin || isAuthPath(url.pathname)) {
      return fallbackUrl;
    }

    return `${url.pathname}${url.search}`;
  } catch {
    return fallbackUrl;
  }
};

/**
 * Reads the persisted auth redirect target from session storage.
 * @returns The stored redirect target if one exists.
 */
const getStoredRedirectUrl = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY);
};

/**
 * Persists the auth redirect target in session storage.
 * @param redirectUrl - Redirect target to persist.
 * @returns Nothing.
 */
const setStoredRedirectUrl = (redirectUrl: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, redirectUrl);
};

/**
 * Removes the persisted auth redirect target from session storage.
 * @returns Nothing.
 */
const clearStoredRedirectUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
};

/**
 * Centralized auth redirect utility
 * Handles preserving URLs (including query parameters) when redirecting to sign-in
 */
const useAuthRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Stores a redirect target for later auth completion.
   * @param preservedLocation - Location data to persist.
   * @returns The sanitized redirect target that was stored.
   */
  const persistRedirectUrl = useCallback((preservedLocation: PreservedLocation): string => {
    const redirectUrl = sanitizeRedirectUrl(buildRedirectUrl(preservedLocation), routes.getBaseRoute());
    setStoredRedirectUrl(redirectUrl);
    return redirectUrl;
  }, []);

  /**
   * Redirects user to sign-in while preserving their current location
   * @returns Nothing.
   */
  const redirectToSignIn = useCallback(() => {
    const preservedLocation = {
      pathname: location.pathname,
      search: location.search
    };

    persistRedirectUrl(preservedLocation);

    navigate(routes.auth.getSignInRoute(), {
      state: { 
        from: preservedLocation
      },
      replace: true
    });
  }, [location.pathname, location.search, navigate, persistRedirectUrl]);

  /**
   * Gets the redirect URL from location state after successful auth
   * @param fallbackUrl - URL to use if no 'from' state is available
   * @returns The resolved redirect target.
   */
  const getRedirectUrl = useCallback((fallbackUrl: string = routes.getBaseRoute()): string => {
    const stateRedirectUrl = location.state?.from
      ? buildRedirectUrl(location.state.from as PreservedLocation)
      : null;
    const sanitizedStateRedirectUrl = sanitizeRedirectUrl(stateRedirectUrl, fallbackUrl);

    if (sanitizedStateRedirectUrl !== fallbackUrl) {
      return sanitizedStateRedirectUrl;
    }

    const storedRedirectUrl = getStoredRedirectUrl();
    const sanitizedStoredRedirectUrl = sanitizeRedirectUrl(storedRedirectUrl, fallbackUrl);

    if (storedRedirectUrl && sanitizedStoredRedirectUrl === fallbackUrl) {
      clearStoredRedirectUrl();
    }

    return sanitizedStoredRedirectUrl;
  }, [location.state]);

  /**
   * Clears the stored redirect target when the current page matches it.
   * @returns Nothing.
   */
  const clearRedirectOnArrival = useCallback((): void => {
    const storedRedirectUrl = getStoredRedirectUrl();

    if (!storedRedirectUrl) {
      return;
    }

    const currentUrl = sanitizeRedirectUrl(buildRedirectUrl({
      pathname: location.pathname,
      search: location.search
    }), '');

    if (storedRedirectUrl === currentUrl) {
      clearStoredRedirectUrl();
    }
  }, [location.pathname, location.search]);

  return {
    clearRedirectOnArrival,
    redirectToSignIn,
    getRedirectUrl
  };
};

export default useAuthRedirect;
